import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Card,
  CardContent,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Save as SaveIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Badge as BadgeIcon,
  PersonPin as PersonPinIcon,
  Settings as SettingsIcon,
  LockOutlined as LockOutlinedIcon,
} from '@mui/icons-material';
import { useMaterialUIController, useGlobalMDAlert } from 'context';
import { useApi } from 'lib/api';
import { tienePermisosModulo } from 'lib/seguridad/permisosPorModulo';
import { FP, PageHeader, fpInputSx } from 'components/financepro';
import ListadoClientesSuplidores from './ListadoClientesSuplidores';
import ConfiguracionClientes from './ConfiguracionClientes';

const RegistroClientes = () => {
  const [controller] = useMaterialUIController();
  const { nombreNegocio, roleDetalles } = controller;
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();
  const showSuccess = (message) =>
    showAlert({ message, color: 'success', autoHideDuration: 3000 });
  const showError = (message) =>
    showAlert({ message, color: 'error', autoHideDuration: 4000 });
  const selectedCompany = nombreNegocio;

  // Estados del formulario
  const [tipoClienteSuplidor, setTipoClienteSuplidor] = useState('Cliente');
  const [nombre, setNombre] = useState('');
  const [direccion, setDireccion] = useState('');
  const [ciudad, setCiudad] = useState('');
  const [telefono, setTelefono] = useState('');
  const [fax, setFax] = useState('');
  const [email, setEmail] = useState('');
  const [rnc, setRnc] = useState('');

  // Estados auxiliares
  const [ciudades, setCiudades] = useState([]);
  const [rncHabilitado, setRncHabilitado] = useState(true);
  const [modoEdicion, setModoEdicion] = useState(false);

  // Estados para búsqueda
  const [dialogoBusqueda, setDialogoBusqueda] = useState(false);
  const [resultadosBusqueda, setResultadosBusqueda] = useState([]);
  const [descripcionBusqueda, setDescripcionBusqueda] = useState('');
  const [tabActual, setTabActual] = useState(0);
  const [clienteIdParaConfiguracion, setClienteIdParaConfiguracion] = useState(null);

  // Referencias para autofocus
  const nombreRef = useRef(null);

  // Lista de ciudades de República Dominicana (del código Python)
  const CIUDADES_DOMINICANAS = [
    "Santiago", "Azua", "Bahoruco", "Barahona", "Dajabon", "Distrito Nacional",
    "Duarte", "Elias Pina", "El Seybo", "Espaillat", "Hato Mayor", "Salcedo",
    "Independencia", "La Altagracia", "La Romana", "La Vega", "Maria Trinidad Sanchez",
    "Mosenor Nouel", "Montecristi", "Monteplata", "Pedernales", "Peravia",
    "Puerto Plata", "Samana", "San Cristobal", "San Jose de Ocoa", "San Juan",
    "Sanchez Ramirez", "Santiago Rodriguez", "Santo Domingo", "Valverde"
  ];

  useEffect(() => {
    setCiudades(CIUDADES_DOMINICANAS);
  }, []);

  const convertirAMayusculas = (texto) => texto.toUpperCase();

  const handleNombreChange = (e) => {
    setNombre(convertirAMayusculas(e.target.value));
  };

  const formatearTelefono = (valor) => {
    const numeros = valor.replace(/\D/g, '');
    if (numeros.length <= 3) return numeros;
    if (numeros.length <= 6) return `${numeros.slice(0, 3)}-${numeros.slice(3)}`;
    return `${numeros.slice(0, 3)}-${numeros.slice(3, 6)}-${numeros.slice(6, 10)}`;
  };

  const handleTelefonoChange = (e) => setTelefono(formatearTelefono(e.target.value));
  const handleFaxChange = (e) => setFax(formatearTelefono(e.target.value));

  const validarFormulario = () => {
    if (!nombre.trim()) {
      showError('El nombre es requerido');
      if (nombreRef.current) nombreRef.current.focus();
      return false;
    }
    if (!direccion.trim()) {
      showError('La dirección es requerida');
      return false;
    }
    if (!email.trim()) {
      showError('El email es requerido');
      return false;
    }
    if (!rnc.trim()) {
      showError('El RNC es requerido');
      return false;
    }
    return true;
  };

  const limpiarFormulario = () => {
    setNombre('');
    setDireccion('');
    setCiudad('');
    setTelefono('');
    setFax('');
    setEmail('');
    setRnc('');
    setRncHabilitado(true);
    setModoEdicion(false);
    setDescripcionBusqueda('');
    if (nombreRef.current) nombreRef.current.focus();
  };

  const handleGuardar = async () => {
    if (!validarFormulario()) return;

    try {
      const { ok, data } = await post('/api/clientesbackend_guardar', {
        id_empresa: selectedCompany,
        nombre,
        direccion,
        ciudad,
        telefono,
        fax,
        email,
        vendedor: '',
        rnc,
        limcredito: 0,
        limcompra: 0,
        desc1: 0,
        desc2: 0,
        desc3: 0,
        tipo: tipoClienteSuplidor,
        area: null,
      });

      if (ok) {
        showSuccess(data.mensaje || 'Datos guardados exitosamente');
        limpiarFormulario();
      } else {
        showError(data.Error || 'Error al guardar los datos');
      }
    } catch (error) {
      showError('Error de conexión al guardar');
    }
  };

  const handleModificar = async () => {
    if (!rnc.trim()) {
      showError('Debe buscar y seleccionar un cliente o suplidor para modificar');
      return;
    }
    if (!validarFormulario()) return;

    try {
      const { ok, data } = await post('/api/clientesbackend_modificar', {
        id_empresa: selectedCompany,
        nombre,
        direccion,
        ciudad,
        telefono,
        fax,
        email,
        vendedor: '',
        rnc,
        limcredito: 0,
        limcompra: 0,
        desc1: 0,
        desc2: 0,
        desc3: 0,
        tipo: tipoClienteSuplidor,
        area: null,
      });

      if (ok) {
        showSuccess(data.mensaje || 'Datos modificados exitosamente');
        limpiarFormulario();
      } else {
        showError(data.Error || 'Error al modificar los datos');
      }
    } catch (error) {
      showError('Error de conexión al modificar');
    }
  };

  const handleEliminar = async () => {
    if (!rnc.trim()) {
      showError('Debe buscar y seleccionar un cliente o suplidor para eliminar');
      return;
    }
    if (!window.confirm('¿Está seguro que desea eliminar este cliente/suplidor?')) {
      return;
    }

    try {
      const { ok, data } = await post('/api/clientesbackend_eliminar', {
        id_empresa: selectedCompany,
        rnc,
        tipo: tipoClienteSuplidor,
      });

      if (ok) {
        showSuccess(data.mensaje || 'Cliente/Suplidor eliminado exitosamente');
        limpiarFormulario();
      } else {
        showError(data.Error || 'Error al eliminar');
      }
    } catch (error) {
      showError('Error de conexión al eliminar');
    }
  };

  const handleBuscar = async () => {
    setDialogoBusqueda(true);
    setResultadosBusqueda([]);
    setDescripcionBusqueda('');
  };

  const realizarBusqueda = async () => {
    try {
      const { ok, data } = await post('/api/clientesbackend_buscar', {
        id_empresa: selectedCompany,
        tipo: tipoClienteSuplidor,
        descripcion: descripcionBusqueda,
      });

      if (ok) {
        setResultadosBusqueda(data.resultados || []);
      } else {
        showError(data.Error || 'Error al buscar');
      }
    } catch (error) {
      showError('Error de conexión al buscar');
    }
  };

  const seleccionarCliente = async (clienteId) => {
    try {
      const { ok, data } = await post('/api/clientesbackend_obtenerporid', {
        id_empresa: selectedCompany,
        id: clienteId,
      });

      if (ok) {
        const cliente = data.cliente;

        setNombre(cliente.descrip || '');
        setDireccion(cliente.direccion || '');
        setCiudad(cliente.ciudad || '');
        setTelefono(cliente.telefono || '');
        setFax(cliente.fax || '');
        setEmail(cliente.email || '');
        setRnc(cliente.rnc || '');
        setTipoClienteSuplidor(cliente.tiposuplidor || 'Cliente');

        setRncHabilitado(false);
        setModoEdicion(true);
        setDialogoBusqueda(false);
        setClienteIdParaConfiguracion(cliente.id);
      } else {
        showError(data.Error || 'Error al obtener cliente');
      }
    } catch (error) {
      showError('Error de conexión al obtener cliente');
    }
  };

  const handleSeleccionarDesdeListado = async (clienteId) => {
    await seleccionarCliente(clienteId);
    setTabActual(0);
  };

  const tienePermiso = tienePermisosModulo(roleDetalles, 'registroclientessuplidores');
  const tienePermisoMantenimiento = tienePermisosModulo(roleDetalles, 'mantenimientoclientessuplidores');
  const tienePermisoConfiguracion = tienePermisosModulo(roleDetalles, 'configuracionclientessuplidores');

  // Botón primario reutilizable estilo FinancePro (navy lleno, sin sombra).
  const primaryBtnSx = {
    bgcolor: FP.color.navy,
    color: '#FFFFFF',
    borderRadius: 1.4,
    height: 46,
    px: 2.4,
    boxShadow: 'none',
    textTransform: 'none',
    fontWeight: 700,
    '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
    '&.Mui-disabled': { bgcolor: '#E2E8F0', color: '#94A3B8' },
  };

  // Botón secundario / outlined (blanco con borde suave).
  const outlineBtnSx = {
    borderColor: FP.color.border,
    color: FP.color.textPrimary,
    bgcolor: '#FFFFFF',
    borderRadius: 1.4,
    height: 46,
    px: 2.4,
    textTransform: 'none',
    fontWeight: 700,
    '&:hover': { bgcolor: FP.color.surfaceAlt, borderColor: FP.color.borderStrong },
  };

  if (!tienePermiso) {
    return (
      <Box
        component="main"
        sx={{
          minHeight: '100vh',
          bgcolor: FP.color.surface,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: { xs: 2, sm: 3, md: 4 },
        }}
      >
        <Card
          elevation={0}
          sx={{
            maxWidth: 560,
            width: '100%',
            borderRadius: 2.5,
            border: `1px solid ${FP.color.border}`,
            bgcolor: FP.color.cardBg,
            boxShadow: FP.shadow.soft,
            textAlign: 'center',
            p: { xs: 3, md: 4 },
          }}
        >
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: 1.6,
              mx: 'auto',
              mb: 2.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: FP.color.dangerSoft,
              color: FP.color.danger,
            }}
          >
            <LockOutlinedIcon sx={{ fontSize: 30 }} />
          </Box>
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: '1.25rem',
              color: FP.color.textPrimary,
              mb: 1,
              letterSpacing: '-0.01em',
            }}
          >
            Acceso Denegado
          </Typography>
          <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.95rem', mb: 1.2 }}>
            No tiene permisos para acceder al módulo de Registro de Clientes y Suplidores.
          </Typography>
          <Typography sx={{ color: FP.color.textMuted, fontSize: '0.85rem' }}>
            Por favor, contacte al administrador del sistema para solicitar los permisos necesarios.
          </Typography>
        </Card>
      </Box>
    );
  }

  return (
    <Box
      component="main"
      sx={{
        minHeight: '100vh',
        bgcolor: FP.color.surface,
      }}
    >
      <PageHeader
        title="Clientes y Suplidores"
        subtitle="Registro y consulta"
      />

      <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
        {/* Tabs */}
        <Card
          elevation={0}
          sx={{
            mb: 3,
            borderRadius: 2.5,
            border: `1px solid ${FP.color.border}`,
            bgcolor: FP.color.cardBg,
            boxShadow: FP.shadow.soft,
            overflow: 'hidden',
          }}
        >
          <Tabs
            value={tabActual}
            onChange={(e, newValue) => setTabActual(newValue)}
            sx={{
              minHeight: 48,
              borderBottom: `1px solid ${FP.color.border}`,
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 700,
                fontSize: '0.92rem',
                color: FP.color.textSecondary,
                minHeight: 48,
                px: 2.4,
              },
              '& .MuiTab-root.Mui-selected': { color: FP.color.navy },
              '& .MuiTabs-indicator': { backgroundColor: FP.color.navy, height: 3 },
            }}
          >
            <Tab icon={<EditIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Registro" />
            {tienePermisoMantenimiento && (
              <Tab icon={<SearchIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Listado" />
            )}
            {tienePermisoConfiguracion && (
              <Tab icon={<SettingsIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Configuración" />
            )}
          </Tabs>
        </Card>

        {/* Tab 0 - Registro */}
        {tabActual === 0 && (
          <Card
            elevation={0}
            sx={{
              borderRadius: 2.5,
              border: `1px solid ${FP.color.border}`,
              bgcolor: FP.color.cardBg,
              boxShadow: FP.shadow.soft,
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                px: { xs: 2, md: 3 },
                py: 1.6,
                borderBottom: `1px solid ${FP.color.border}`,
                bgcolor: '#FAFBFE',
                display: 'flex',
                alignItems: 'center',
                gap: 1.2,
              }}
            >
              <Box
                sx={{
                  width: 30,
                  height: 30,
                  borderRadius: 1.2,
                  bgcolor: FP.color.accentBlueSoft,
                  color: FP.color.accentBlue,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {tipoClienteSuplidor === 'Cliente' ? (
                  <PersonIcon sx={{ fontSize: 18 }} />
                ) : (
                  <BusinessIcon sx={{ fontSize: 18 }} />
                )}
              </Box>
              <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
                Datos del {tipoClienteSuplidor}
              </Typography>
              <Box sx={{ flex: 1 }} />
              <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.78rem', fontWeight: 600 }}>
                {modoEdicion ? 'Modo edición' : 'Nuevo registro'}
              </Typography>
            </Box>

            <CardContent sx={{ p: { xs: 2, md: 3 } }}>
              <Grid container spacing={2.5}>
                {/* Tipo, Nombre, Buscar */}
                <Grid item xs={12} sm={3}>
                  <FormControl fullWidth sx={fpInputSx}>
                    <InputLabel id="tipo-cs-label">Tipo</InputLabel>
                    <Select
                      labelId="tipo-cs-label"
                      value={tipoClienteSuplidor}
                      onChange={(e) => setTipoClienteSuplidor(e.target.value)}
                      label="Tipo"
                    >
                      <MenuItem value="Cliente">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PersonIcon fontSize="small" />
                          Cliente
                        </Box>
                      </MenuItem>
                      <MenuItem value="Suplidor">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <BusinessIcon fontSize="small" />
                          Suplidor
                        </Box>
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={7}>
                  <TextField
                    fullWidth
                    label="Nombre"
                    value={nombre}
                    onChange={handleNombreChange}
                    inputRef={nombreRef}
                    required
                    autoFocus
                    InputLabelProps={{ shrink: true }}
                    placeholder="Nombre del cliente o suplidor"
                    InputProps={{
                      startAdornment: (
                        <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                          <PersonPinIcon sx={{ color: FP.color.textMuted, fontSize: 20 }} />
                        </Box>
                      ),
                    }}
                    sx={fpInputSx}
                  />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<SearchIcon />}
                    onClick={handleBuscar}
                    sx={outlineBtnSx}
                  >
                    Buscar
                  </Button>
                </Grid>

                {/* Dirección y Ciudad */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Dirección"
                    value={direccion}
                    onChange={(e) => setDireccion(e.target.value.toUpperCase())}
                    multiline
                    rows={2}
                    required
                    InputLabelProps={{ shrink: true }}
                    placeholder="Calle, sector, referencia"
                    InputProps={{
                      startAdornment: (
                        <Box sx={{ mr: 1, display: 'flex', alignItems: 'flex-start', pt: 1 }}>
                          <LocationIcon sx={{ color: FP.color.textMuted, fontSize: 20 }} />
                        </Box>
                      ),
                    }}
                    sx={{
                      ...fpInputSx,
                      '& .MuiOutlinedInput-root': {
                        ...fpInputSx['& .MuiOutlinedInput-root'],
                        height: 'auto',
                        minHeight: 46,
                        alignItems: 'flex-start',
                        py: 1,
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth sx={fpInputSx}>
                    <InputLabel id="ciudad-label">Ciudad</InputLabel>
                    <Select
                      labelId="ciudad-label"
                      value={ciudad}
                      onChange={(e) => setCiudad(e.target.value)}
                      label="Ciudad"
                    >
                      {ciudades.map((ciudadItem) => (
                        <MenuItem key={ciudadItem} value={ciudadItem}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LocationIcon sx={{ fontSize: 18, color: FP.color.textMuted }} />
                            {ciudadItem}
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Teléfono, Whatsapp, Email */}
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Teléfono"
                    value={telefono}
                    onChange={handleTelefonoChange}
                    placeholder="999-999-9999"
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      startAdornment: (
                        <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                          <PhoneIcon sx={{ color: FP.color.textMuted, fontSize: 20 }} />
                        </Box>
                      ),
                    }}
                    sx={fpInputSx}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Whatsapp / Celular"
                    value={fax}
                    onChange={handleFaxChange}
                    placeholder="999-999-9999"
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      startAdornment: (
                        <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                          <PhoneIcon sx={{ color: FP.color.textMuted, fontSize: 20 }} />
                        </Box>
                      ),
                    }}
                    sx={fpInputSx}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    type="email"
                    required
                    InputLabelProps={{ shrink: true }}
                    placeholder="correo@empresa.com"
                    InputProps={{
                      startAdornment: (
                        <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                          <EmailIcon sx={{ color: FP.color.textMuted, fontSize: 20 }} />
                        </Box>
                      ),
                    }}
                    sx={fpInputSx}
                  />
                </Grid>

                {/* RNC */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="RNC / Cédula"
                    value={rnc}
                    onChange={(e) => {
                      const valor = e.target.value.replace(/\D/g, '').slice(0, 12);
                      setRnc(valor);
                    }}
                    disabled={!rncHabilitado}
                    required
                    InputLabelProps={{ shrink: true }}
                    placeholder="Sólo números"
                    InputProps={{
                      startAdornment: (
                        <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                          <BadgeIcon sx={{ color: FP.color.textMuted, fontSize: 20 }} />
                        </Box>
                      ),
                    }}
                    sx={fpInputSx}
                  />
                </Grid>
              </Grid>

              {/* Botones de acción */}
              <Box
                sx={{
                  display: 'flex',
                  gap: 1.2,
                  justifyContent: 'flex-end',
                  flexWrap: 'wrap',
                  mt: 3,
                  pt: 2.5,
                  borderTop: `1px solid ${FP.color.border}`,
                }}
              >
                <Button
                  variant="outlined"
                  startIcon={<ClearIcon />}
                  onClick={limpiarFormulario}
                  sx={outlineBtnSx}
                >
                  Limpiar
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<DeleteIcon />}
                  onClick={handleEliminar}
                  disabled={!modoEdicion}
                  sx={{
                    ...outlineBtnSx,
                    borderColor: FP.color.danger,
                    color: FP.color.danger,
                    '&:hover': {
                      bgcolor: FP.color.dangerSoft,
                      borderColor: FP.color.danger,
                    },
                    '&.Mui-disabled': {
                      borderColor: FP.color.border,
                      color: FP.color.textMuted,
                    },
                  }}
                >
                  Eliminar
                </Button>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={modoEdicion ? handleModificar : handleGuardar}
                  sx={primaryBtnSx}
                >
                  {modoEdicion ? 'Modificar' : 'Guardar'}
                </Button>
              </Box>
            </CardContent>
          </Card>
        )}

        {tienePermisoMantenimiento && tabActual === 1 && (
          <ListadoClientesSuplidores onSeleccionarCliente={handleSeleccionarDesdeListado} />
        )}

        {tienePermisoConfiguracion && tabActual === 2 && (
          <ConfiguracionClientes
            clienteId={clienteIdParaConfiguracion}
            onGuardar={() => {
              if (clienteIdParaConfiguracion) {
                seleccionarCliente(clienteIdParaConfiguracion);
              }
            }}
          />
        )}

        {/* Diálogo de Búsqueda */}
        <Dialog
          open={dialogoBusqueda}
          onClose={() => setDialogoBusqueda(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2.5,
              border: `1px solid ${FP.color.border}`,
              boxShadow: FP.shadow.md,
            },
          }}
        >
          <DialogTitle
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 3,
              py: 2,
              borderBottom: `1px solid ${FP.color.border}`,
              bgcolor: '#FAFBFE',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
              <Box
                sx={{
                  width: 30,
                  height: 30,
                  borderRadius: 1.2,
                  bgcolor: FP.color.accentBlueSoft,
                  color: FP.color.accentBlue,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <SearchIcon sx={{ fontSize: 18 }} />
              </Box>
              <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: FP.color.textPrimary }}>
                Buscar {tipoClienteSuplidor === 'Cliente' ? 'Clientes' : 'Suplidores'}
              </Typography>
            </Box>
            <IconButton
              onClick={() => setDialogoBusqueda(false)}
              size="small"
              sx={{
                color: FP.color.textSecondary,
                border: `1px solid ${FP.color.border}`,
                borderRadius: 1.2,
                width: 32,
                height: 32,
                '&:hover': { bgcolor: FP.color.surfaceAlt, color: FP.color.navy },
              }}
            >
              <ClearIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 3 }}>
            <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
              <Grid item xs={12} sm={9}>
                <TextField
                  fullWidth
                  label="Descripción"
                  value={descripcionBusqueda}
                  onChange={(e) => setDescripcionBusqueda(e.target.value)}
                  placeholder="Buscar por nombre…"
                  InputLabelProps={{ shrink: true }}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') realizarBusqueda();
                  }}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<SearchIcon />}
                  onClick={realizarBusqueda}
                  sx={primaryBtnSx}
                >
                  Buscar
                </Button>
              </Grid>
            </Grid>

            <TableContainer
              component={Paper}
              elevation={0}
              sx={{
                borderRadius: 2,
                border: `1px solid ${FP.color.border}`,
                boxShadow: 'none',
              }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: FP.color.surfaceAlt }}>
                    <TableCell
                      sx={{
                        fontWeight: 700,
                        color: FP.color.textSecondary,
                        textTransform: 'uppercase',
                        fontSize: '0.72rem',
                        letterSpacing: '0.08em',
                      }}
                    >
                      ID
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 700,
                        color: FP.color.textSecondary,
                        textTransform: 'uppercase',
                        fontSize: '0.72rem',
                        letterSpacing: '0.08em',
                      }}
                    >
                      Nombre
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 700,
                        color: FP.color.textSecondary,
                        textTransform: 'uppercase',
                        fontSize: '0.72rem',
                        letterSpacing: '0.08em',
                      }}
                    >
                      RNC
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 700,
                        color: FP.color.textSecondary,
                        textTransform: 'uppercase',
                        fontSize: '0.72rem',
                        letterSpacing: '0.08em',
                      }}
                    >
                      Ciudad
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        fontWeight: 700,
                        color: FP.color.textSecondary,
                        textTransform: 'uppercase',
                        fontSize: '0.72rem',
                        letterSpacing: '0.08em',
                      }}
                    >
                      Acción
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {resultadosBusqueda.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={5}
                        align="center"
                        sx={{ color: FP.color.textMuted, py: 4, fontSize: '0.85rem' }}
                      >
                        Sin resultados. Escriba una descripción y presione Buscar.
                      </TableCell>
                    </TableRow>
                  ) : (
                    resultadosBusqueda.map((cliente) => (
                      <TableRow
                        key={cliente.id}
                        sx={{ '&:hover': { bgcolor: FP.color.surface } }}
                      >
                        <TableCell sx={{ color: FP.color.textPrimary }}>{cliente.id}</TableCell>
                        <TableCell sx={{ color: FP.color.textPrimary, fontWeight: 600 }}>
                          {cliente.descrip}
                        </TableCell>
                        <TableCell sx={{ color: FP.color.textPrimary }}>{cliente.rnc}</TableCell>
                        <TableCell sx={{ color: FP.color.textPrimary }}>{cliente.ciudad}</TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            variant="contained"
                            startIcon={<EditIcon sx={{ fontSize: 16 }} />}
                            onClick={() => seleccionarCliente(cliente.id)}
                            sx={{
                              bgcolor: FP.color.navy,
                              color: '#FFFFFF',
                              borderRadius: 1.2,
                              textTransform: 'none',
                              fontWeight: 700,
                              boxShadow: 'none',
                              '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
                            }}
                          >
                            Seleccionar
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${FP.color.border}` }}>
            <Button onClick={() => setDialogoBusqueda(false)} sx={outlineBtnSx}>
              Cerrar
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default RegistroClientes;
